/**
 * aC Men's Hub の「案件」で、全社スプシへの反映を止める時間帯（自動の書き写し・工程の連携）の語彙と判定。
 *
 * 判定は日本時間で行う。API（反映を受け付けるか）と Web（下の帯の「HH:50 から反映できます」）が同じ関数を使う。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 全社スプシへの反映を止める時間帯の設定の形と、今が止める時間帯かを日本時間で判定する関数を API と Web で共有する
 * @graph-connects none
 */
import { z } from 'zod';
/**
 * 反映しない時間帯の理由。
 * - backlog_sync: Backlog からスプシへの自動の書き写し（平日の毎時30分に始まる）
 * - process_sync: 工程の連携（12時台に書く）
 * @graph-connects none
 */
export const ProductBacklogWriteBlockReasonSchema = z.enum(['backlog_sync', 'process_sync']);
/**
 * 反映しない時間帯1つ（日本時間）。days の曜日で、fromHour〜toHour 時台の fromMinute〜toMinute 分は反映しない。
 * weekdays は月〜金（祝日も含む。書き写しが祝日も動くため）。
 * @graph-connects none
 */
export const ProductBacklogWriteWindowSchema = z
    .object({
    days: z.enum(['weekdays', 'everyday']),
    fromHour: z.number().int().min(0).max(23),
    toHour: z.number().int().min(0).max(23),
    fromMinute: z.number().int().min(0).max(59),
    toMinute: z.number().int().min(0).max(59),
    reason: ProductBacklogWriteBlockReasonSchema,
})
    .refine((window) => window.fromHour <= window.toHour && window.fromMinute <= window.toMinute);
/**
 * 反映しない時間帯の既定（設定で変えられる）。平日9〜21時台の毎時25〜49分（書き写しは毎時30分に始まり、
 * 待ち上限600秒と再試行の余裕を見る）と、毎日12時台（工程の連携）。
 * @graph-connects none
 */
export const DEFAULT_PRODUCT_BACKLOG_WRITE_WINDOWS = [
    {
        days: 'weekdays',
        fromHour: 9,
        toHour: 21,
        fromMinute: 25,
        toMinute: 49,
        reason: 'backlog_sync',
    },
    {
        days: 'everyday',
        fromHour: 12,
        toHour: 12,
        fromMinute: 0,
        toMinute: 59,
        reason: 'process_sync',
    },
];
/**
 * 日本時間（UTC+9。夏時間なし）のずれ。
 * @graph-connects none
 */
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
/**
 * 1分。
 * @graph-connects none
 */
const MINUTE_MS = 60 * 1000;
/**
 * その分（日本時間）が当てはまる反映しない時間帯（無ければ null）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business ある時刻を日本時間の曜日・時・分に直し、反映しない時間帯のどれに当たるかを返す
 * @graph-connects none
 */
function windowAt(time, windows) {
    const jst = new Date(time + JST_OFFSET_MS);
    const day = jst.getUTCDay();
    const hour = jst.getUTCHours();
    const minute = jst.getUTCMinutes();
    const weekday = day >= 1 && day <= 5;
    return (windows.find((window) => (window.days === 'everyday' || weekday) &&
        hour >= window.fromHour &&
        hour <= window.toHour &&
        minute >= window.fromMinute &&
        minute <= window.toMinute) ?? null);
}
/**
 * 今が反映しない時間帯なら、反映できるようになる時刻（ISO 8601）と理由を返す（反映できるなら null）。
 * 時間帯が続いていれば（平日12:25 は書き写しと工程の連携の両方）、どれにも当たらない最初の分まで進め、
 * 理由は最後に当たっていた時間帯のものにする（12:25 なら「13:00 から・工程の連携」）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 今が全社スプシへの反映を止める時間帯（自動の書き写し・工程の連携）かを日本時間で判定し、反映できるようになる時刻と理由を返す
 * @graph-connects windowAt [calls] その分が当たる時間帯
 */
export function findWriteBlock(now, windows) {
    let time = Math.floor(now.getTime() / MINUTE_MS) * MINUTE_MS;
    let last = windowAt(time, windows);
    if (last === null)
        return null;
    // 1日（1440分）進めても抜けない設定は、時間帯が1日中を覆っている。その日の終わりで打ち切る
    for (let step = 0; step < 24 * 60; step += 1) {
        const next = windowAt(time + MINUTE_MS, windows);
        time += MINUTE_MS;
        if (next === null)
            break;
        last = next;
    }
    return { resumeAt: new Date(time).toISOString(), reason: last.reason };
}
//# sourceMappingURL=product-backlog-window.js.map